import { Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './rbac/ProtectedRoute';
import { LoginPage } from './pages/Login';
import { MfaChallengePage } from './pages/MfaChallenge';
import { NoAccessPage } from './pages/NoAccess';
import { ForbiddenPage } from './pages/Forbidden';
import { NotFoundPage } from './pages/NotFound';
import { RootRedirect } from './pages/RootRedirect';
import { BrandDashboard } from './pages/BrandDashboard';
import { UsersPage } from './pages/Users';
import { ApiKeysPage } from './pages/ApiKeys';
import { AuditLogPage } from './pages/AuditLog';
import { CancelIntentsPage } from './pages/CancelIntents';
import { AppSettingsPage } from './pages/AppSettings';
import { CoreSettingsOverview } from './pages/CoreSettings';
import { TaxRatesPage } from './pages/TaxRates';
import { ResellersPage } from './pages/Resellers';
import { AdminOverviewPage } from './pages/AdminOverview';
import { NexusPluginsPage } from './pages/NexusPlugins';
import { PodClustersPage } from './pages/PodClusters';
import { EnhanceAdminPage } from './pages/EnhanceAdmin';
import { ServerCapacityPage } from './pages/ServerCapacity';
import { NexusTenantRoutingPage } from './pages/NexusTenantRouting';
import { NexusSettingsPage } from './pages/NexusSettings';
import { NexusJobsPage } from './pages/NexusJobs';
import { NexusSagaReviewPage } from './pages/NexusSagaReview';
import { NexusMailSecurityPage } from './pages/NexusMailSecurity';
import { NexusPostmasterPage } from './pages/NexusPostmaster';
import { NexusUsagePage } from './pages/NexusUsage';
import { NexusCustomersPage } from './pages/NexusCustomers';
import { NexusHostingOpsPage } from './pages/NexusHostingOps';
import { NexusDedicatedPage } from './pages/NexusDedicated';
import { NexusGatewayAllowlistPage } from './pages/NexusGatewayAllowlist';
import { NexusCrowdsecPage } from './pages/NexusCrowdsec';
import { NexusInisPage } from './pages/NexusInis';
import { NexusMailReviewPage } from './pages/NexusMailReview';
import { OutboundSendersPage } from './pages/OutboundSenders';
import { NexusBulkBoxesPage } from './pages/NexusBulkBoxes';
import { NexusVpsNodesPage } from './pages/NexusVpsNodes';
import { NexusDmarcPage } from './pages/NexusDmarc';
import { NexusBaseStatsPage } from './pages/NexusBaseStats';
import { NexusBackupStatsPage } from './pages/NexusBackupStats';
import { NexusNetfabricStatsPage } from './pages/NexusNetfabricStats';
import { NexusManagedDbStatsPage } from './pages/NexusManagedDbStats';
import { NexusDeployStatsPage } from './pages/NexusDeployStats';
import { NexusLearningStatsPage } from './pages/NexusLearningStats';
import { NexusMigrationStatsPage } from './pages/NexusMigrationStats';
import { NexusComplianceStatsPage } from './pages/NexusComplianceStats';
import { NexusOverviewPage } from './pages/NexusOverview';
import { PulseSecurityPage } from './pages/PulseSecurity';
import { PulseCrowdsecPage } from './pages/PulseCrowdsec';
import { KnowledgeBasePage } from './pages/Knowledge';
import { OpsKbPage } from './pages/OpsKb';
import { ValisightCurationPage, ValisightAlertRulesPage } from './pages/Valisight';
import {
  PulseOverviewPage,
  PulseZabbixPage,
  PulseTelemetryPage,
  PulseCpGuardPage,
} from './pages/PulseOverview';
import {
  ProbeOverviewPage,
  ProbeScrapePage,
  ProbeWireguardPage,
  ProbeKedaPage,
} from './pages/ProbeSettings';
import {
  EventsOverviewPage,
  EventsSourcesPage,
  EventsTestPage,
  EventsCatalogPage,
} from './pages/EventsApi';
import {
  EbillOverviewPage,
  EbillGeneratePage,
  EbillValidatePage,
  EbillArtifactsPage,
  EbillStatsPage,
  EbillSettingsPage,
} from './pages/EbillApi';
import {
  PriceOverviewPage,
  PriceSourcesPage,
  PriceTestPage,
  PriceCatalogPage,
  PriceStoresPage,
  PriceStatsPage,
} from './pages/PriceApi';
import {
  WpOverviewPage,
  WpPluginsPage,
  WpTemplatesPage,
  WpInstancesPage,
  WpTelemetryPage,
  WpModulesPage,
  WpSecurityPage,
  WpCompliancePage,
  WpVulnPage,
} from './pages/WpApi';
import { SubservicesPage } from './pages/Subservices';
import {
  GeocodeApiPage,
  TranslateApiPage,
  LlmApiPage,
  TrademarkApiPage,
  PlatformApiPage,
} from './pages/ApiProducts';
import {
  SpamApiPage, A11yApiPage, ScanApiPage, SearchApiPage,
} from './pages/ProductApis';
import {
  MtffInstancesPage,
  MtffThemeEditorPage,
  MtffCustomCssEditorPage,
  MtffPageThemesPage,
  MtffPluginsPage,
  MtffSourcesPage,
  MtffSecurityPage,
  MtffOpsPage,
  MtffWebhooksPage,
} from './pages/MtffInstances';
import { DiagnosticsPage } from './pages/Diagnostics';
import { MeLayout, MeProfilePage, MeSecurityPage } from './pages/Me';
import { TenantSettingsPage } from './pages/BrandSettings';
import { OutboundWebhooksPage } from './pages/OutboundWebhooks';
import { OrdersPage } from './pages/Orders';
import { SubscriptionsPage } from './pages/Subscriptions';
import { DriversPage } from './pages/Drivers';
import { UsagePage } from './pages/Usage';
import { InfrastructurePage } from './pages/Infrastructure';
import { OssReportPage } from './pages/OssReport';
import { StripeConnectPage } from './pages/StripeConnect';
import { PolicyPage } from './pages/Policy';
import { ProductsPage } from './pages/Products';
import { UsageRunsPage } from './pages/UsageRuns';
import { ApiCatalogPage } from './pages/ApiCatalog/ApiCatalog';
import { TicketsPage } from './pages/Tickets';
import { IntegrationsPage } from './pages/Integrations';
import { MainShell } from '../MainShell';
import { BrandShellSync } from '../BrandShellSync';

/**
 * Single source of truth für die Route-Hierarchie.
 *
 * Mapping zur alten Core-Admin-UI siehe `nav.js`. Specs in
 * `lmm-wiki/wiki/orbit-specs/` definieren die Detail-Anforderungen pro View.
 */
export function OrbitRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/mfa-challenge" element={<MfaChallengePage />} />
      <Route path="/no-access" element={<NoAccessPage />} />
      <Route path="/403" element={<ForbiddenPage />} />

      {/* Shell-wrapped */}
      <Route element={<MainShell />}>
        <Route index element={<RootRedirect />} />

        {/* /:brandSlug/* — brand-kontextuell */}
        <Route path=":brandSlug" element={<ProtectedRoute requiresBrand />}>
          <Route element={<BrandShellSync />}>
            {/* Dashboard */}
            <Route element={<ProtectedRoute perms={['dashboard.read']} />}>
              <Route path="dashboard" element={<BrandDashboard />} />
            </Route>

            {/* Tickets — Backend: orbit.api.valitype.net (Postgres). Spec:
                lmm-wiki/wiki/orbit-specs/done/orbit-tickets.md. CORE liefert
                tickets.read seit core-tickets-permissions (2026-06-14)
                für staff+admin. */}
            <Route element={<ProtectedRoute perms={['tickets.read']} />}>
              <Route path="tickets" element={<TicketsPage />} />
              <Route path="tickets/:ticketId" element={<TicketsPage />} />
            </Route>

            {/* Interne Ops-Wissensbasis / Runbook-Hub (kb.read = staff+admin) */}
            <Route element={<ProtectedRoute perms={['kb.read']} />}>
              <Route path="ops-kb" element={<OpsKbPage />} />
            </Route>

            {/* Users — spec: user-crud-admin */}
            <Route element={<ProtectedRoute perms={['users.read']} />}>
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:userId" element={<UsersPage />} />
            </Route>

            {/* Orders & Billing */}
            <Route element={<ProtectedRoute perms={['orders.read']} />}>
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/:orderId" element={<OrdersPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['subscriptions.read']} />}>
              <Route path="subscriptions" element={<SubscriptionsPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['cancelIntents.read']} />}>
              <Route path="cancel-intents" element={<CancelIntentsPage />} />
            </Route>

            {/* Catalog */}
            <Route element={<ProtectedRoute perms={['products.read']} />}>
              <Route path="products" element={<ProductsPage />} />
              <Route path="usage-runs" element={<UsageRunsPage />} />
              <Route path="api-catalog" element={<ApiCatalogPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['usage.read']} />}>
              <Route path="usage" element={<UsagePage />} />
            </Route>

            {/* API-Keys */}
            <Route element={<ProtectedRoute perms={['apiKeys.read']} />}>
              <Route path="api-keys" element={<ApiKeysPage />} />
            </Route>

            {/* Integrations */}
            <Route element={<ProtectedRoute perms={['drivers.read']} />}>
              <Route path="drivers" element={<DriversPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['integrations.read']} />}>
              <Route path="integrations" element={<IntegrationsPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['stripeConnect.read']} />}>
              <Route path="stripe-connect" element={<StripeConnectPage />} />
            </Route>

            {/* Outbound-Webhooks — spec: outbound-webhooks-admin (done) */}
            <Route element={<ProtectedRoute perms={['outboundWebhooks.read']} />}>
              <Route path="outbound-webhooks" element={<OutboundWebhooksPage />} />
            </Route>

            {/* Compliance */}
            <Route element={<ProtectedRoute perms={['audit.read']} />}>
              <Route path="audit" element={<AuditLogPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['oss.read']} />}>
              <Route path="oss" element={<OssReportPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['policy.read']} />}>
              <Route path="policy" element={<PolicyPage />} />
            </Route>

            {/* Brand-Settings */}
            <Route element={<ProtectedRoute perms={['tenantSettings.read']} />}>
              <Route path="brand-settings" element={<TenantSettingsPage />} />
            </Route>
          </Route>
        </Route>

        {/* /admin/* — system-level, brandless */}
        <Route
          path="admin"
          element={<ProtectedRoute anyOf={['appSettings.read', 'integrations.read']} />}
        >
          <Route index element={<AdminOverviewPage />} />

          <Route element={<ProtectedRoute perms={['appSettings.read']} />}>
            <Route path="infra" element={<InfrastructurePage />} />
            <Route path="diagnostics" element={<DiagnosticsPage />} />
          </Route>

          <Route element={<ProtectedRoute perms={['integrations.read']} />}>
            <Route path="integrations" element={<IntegrationsPage />} />
          </Route>

          <Route path="settings">
            <Route element={<ProtectedRoute perms={['appSettings.read']} />}>
              <Route path="core" element={<CoreSettingsOverview />} />
              <Route path="core/tax-rates" element={<TaxRatesPage />} />
              <Route path="core/resellers" element={<ResellersPage />} />
              <Route path="app" element={<AppSettingsPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['integrations.read']} />}>
              <Route path="nexus" element={<NexusOverviewPage />} />
              <Route path="nexus/plugins" element={<NexusPluginsPage />} />
              <Route path="nexus/routing" element={<NexusTenantRoutingPage />} />
              <Route path="nexus/settings" element={<NexusSettingsPage />} />
              <Route path="nexus/jobs" element={<NexusJobsPage />} />
              <Route path="nexus/saga" element={<NexusSagaReviewPage />} />
              <Route path="nexus/pod-clusters" element={<PodClustersPage />} />
              <Route path="nexus/enhance" element={<EnhanceAdminPage />} />
              <Route path="nexus/server-capacity" element={<ServerCapacityPage />} />
              <Route path="nexus/mail-security" element={<NexusMailSecurityPage />} />
              <Route path="nexus/postmaster" element={<NexusPostmasterPage />} />
              <Route path="nexus/usage" element={<NexusUsagePage />} />
              <Route path="nexus/customers" element={<NexusCustomersPage />} />
              <Route path="nexus/hosting-ops" element={<NexusHostingOpsPage />} />
              <Route path="nexus/dedicated" element={<NexusDedicatedPage />} />
              <Route path="nexus/gateway-allowlist" element={<NexusGatewayAllowlistPage />} />
              <Route path="nexus/crowdsec-lists" element={<NexusCrowdsecPage />} />
              <Route path="nexus/inis" element={<NexusInisPage />} />
              <Route path="nexus/mail-review" element={<NexusMailReviewPage />} />
              <Route path="nexus/outbound-senders" element={<OutboundSendersPage />} />
              <Route path="nexus/bulk-boxes" element={<NexusBulkBoxesPage />} />
              <Route path="nexus/vps-nodes" element={<NexusVpsNodesPage />} />
              <Route path="nexus/dmarc" element={<NexusDmarcPage />} />
              <Route path="nexus/base-stats" element={<NexusBaseStatsPage />} />
              <Route path="nexus/backup-stats" element={<NexusBackupStatsPage />} />
              <Route path="nexus/netfabric-stats" element={<NexusNetfabricStatsPage />} />
              <Route path="nexus/managed-db-stats" element={<NexusManagedDbStatsPage />} />
              <Route path="nexus/deploy-stats" element={<NexusDeployStatsPage />} />
              <Route path="nexus/learning-stats" element={<NexusLearningStatsPage />} />
              <Route path="nexus/migration-stats" element={<NexusMigrationStatsPage />} />
              <Route path="nexus/compliance-stats" element={<NexusComplianceStatsPage />} />
              <Route path="pulse/knowledge" element={<KnowledgeBasePage />} />
              <Route path="valisight/curation" element={<ValisightCurationPage />} />
              <Route path="valisight/alert-rules" element={<ValisightAlertRulesPage />} />
              <Route path="pulse/security" element={<PulseSecurityPage />} />
              <Route path="pulse/crowdsec" element={<PulseCrowdsecPage />} />

              {/* MTFF Theme + Plugin Control-Plane (vault: 02_applications/07_MTFF) */}
              <Route path="mtff/instances" element={<MtffInstancesPage />} />
              <Route path="mtff/instances/:instanceId/theme" element={<MtffThemeEditorPage />} />
              <Route path="mtff/instances/:instanceId/css" element={<MtffCustomCssEditorPage />} />
              <Route path="mtff/instances/:instanceId/page-themes" element={<MtffPageThemesPage />} />
              <Route path="mtff/instances/:instanceId/plugins" element={<MtffPluginsPage />} />
              <Route path="mtff/instances/:instanceId/sources" element={<MtffSourcesPage />} />
              <Route path="mtff/instances/:instanceId/security" element={<MtffSecurityPage />} />
              <Route path="mtff/instances/:instanceId/ops" element={<MtffOpsPage />} />
              <Route path="mtff/webhooks" element={<MtffWebhooksPage />} />

              <Route path="probe" element={<ProbeOverviewPage />} />
              <Route path="probe/scrape" element={<ProbeScrapePage />} />
              <Route path="probe/wireguard" element={<ProbeWireguardPage />} />
              <Route path="probe/keda" element={<ProbeKedaPage />} />

              <Route path="pulse" element={<PulseOverviewPage />} />
              <Route path="pulse/zabbix" element={<PulseZabbixPage />} />
              <Route path="pulse/telemetry" element={<PulseTelemetryPage />} />
              <Route path="pulse/cpguard" element={<PulseCpGuardPage />} />
            </Route>
          </Route>

          {/* Public APIs (valitype.io) — Scraping-Produkte auf Basis von Probe. */}
          <Route path="apis" element={<ProtectedRoute perms={['integrations.read']} />}>
            <Route path="events" element={<EventsOverviewPage />} />
            <Route path="events/sources" element={<EventsSourcesPage />} />
            <Route path="events/test" element={<EventsTestPage />} />
            <Route path="events/catalog" element={<EventsCatalogPage />} />
            <Route path="ebill" element={<EbillOverviewPage />} />
            <Route path="ebill/generate" element={<EbillGeneratePage />} />
            <Route path="ebill/validate" element={<EbillValidatePage />} />
            <Route path="ebill/artifacts" element={<EbillArtifactsPage />} />
            <Route path="ebill/stats" element={<EbillStatsPage />} />
            <Route path="ebill/settings" element={<EbillSettingsPage />} />
            <Route path="price" element={<PriceOverviewPage />} />
            <Route path="price/sources" element={<PriceSourcesPage />} />
            <Route path="price/test" element={<PriceTestPage />} />
            <Route path="price/catalog" element={<PriceCatalogPage />} />
            <Route path="price/stores" element={<PriceStoresPage />} />
            <Route path="price/stats" element={<PriceStatsPage />} />
            <Route path="wp" element={<WpOverviewPage />} />
            <Route path="wp/plugins" element={<WpPluginsPage />} />
            <Route path="wp/templates" element={<WpTemplatesPage />} />
            <Route path="wp/instances" element={<WpInstancesPage />} />
            <Route path="wp/telemetry" element={<WpTelemetryPage />} />
            <Route path="wp/modules" element={<WpModulesPage />} />
            <Route path="wp/security" element={<WpSecurityPage />} />
            <Route path="wp/compliance" element={<WpCompliancePage />} />
            <Route path="wp/vuln" element={<WpVulnPage />} />
            <Route path="subservices" element={<SubservicesPage />} />
            <Route path="geocode" element={<GeocodeApiPage />} />
            <Route path="translate" element={<TranslateApiPage />} />
            <Route path="llm" element={<LlmApiPage />} />
            <Route path="trademark" element={<TrademarkApiPage />} />
            <Route path="platform" element={<PlatformApiPage />} />
            <Route path="spam" element={<SpamApiPage />} />
            <Route path="scan" element={<ScanApiPage />} />
            <Route path="a11y" element={<A11yApiPage />} />
            <Route path="search" element={<SearchApiPage />} />
          </Route>
        </Route>

        <Route path="me" element={<ProtectedRoute />}>
          <Route element={<MeLayout />}>
            <Route index element={<MeProfilePage />} />
            <Route path="security" element={<MeSecurityPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
